import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChange
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';

import {debounceTime, takeUntil} from 'rxjs/operators';

import concat from 'lodash-es/concat';
import defaults from 'lodash-es/defaults';
import includes from 'lodash-es/includes';
import isNil from 'lodash-es/isNil';

import {TreeItem} from './treeview.component';

import {Destroyable} from '../../../destroyable.component';
import {GPTranslateService} from '../../localization/translate.service';

export const sortByAsc = function (bHigherThanA: boolean, asc: boolean) {
  if (bHigherThanA) {
    return asc ? -1 : 1;
  } else {
    return asc ? 1 : -1;
  }
};

class FilterTreeItem extends TreeItem {
  private refItem: TreeItem;

  constructor(item: TreeItem) {
    super(item.text, item.value, item.transIds);
    this.disabled = item.disabled;
    this.checked = item.checked;
    this.collapsed = item.collapsed;
    this.children = item.children;
    this.refItem = item;
  }

  public updateRefChecked() {
    if (!isNil(this.children)) {
      this.children.forEach(child => {
        if (child instanceof FilterTreeItem) {
          child.updateRefChecked();
        }
      });
    }

    let refChecked: any = this.checked;
    if (!isNil(this.refItem.children)) {
      for (let i = 0; i < this.refItem.children.length; i++) {
        const refChild = this.refItem.children[i];
        if (refChild instanceof FilterTreeItem) {
          refChild.updateRefChecked();
        }
        if (!refChild.checked) {
          refChecked = false;
          break;
        }
      }
    }
    this.refItem.checked = refChecked;
  }
}

export interface DropdownTreeviewConfig {
  isShowFilter?: boolean;
  isShowCollapseExpand?: boolean;
  noSelectText?: string;
  moreSelectText?: string;
  countAllChecked?: boolean;
}

export let DefaultConfig: DropdownTreeviewConfig = {
  isShowFilter: false,
  isShowCollapseExpand: false
};

@Component({
  selector: 'gp-tree-multi-select',
  template: `
    <div class="dropdown" [class.open]="isOpen || inline" [class.inline]="inline">
      <div>
        <button class="white form-control dropdown-button text-left p-r-0"
                style="font-size: 0.9rem; min-height: 2rem"
                aria-haspopup="true" aria-expanded="false">
          <div (click)="onButtonClick($event)" class="input-text inline">
            <span>{{ text ? (text | translate) : transIdsDisplay}}</span>
          </div>
          <div class="pull-right inline input-icons">
            <div *ngIf="getCheckedItems()?.length" (click)="resetDropdown()" class="remove-icon">
              <em class="fa fa-light fa-times fa-sm" aria-hidden="true"></em>
            </div>
            <div (click)="onButtonClick($event)" class="w-full p-r-1 p-l-xs">
              <em class="dropdown-toggle"></em>
            </div>
          </div>
        </button>
      </div>
      <div class="dropdown-menu" aria-labelledby="dropdownMenuButton"
           [ngSwitch]="hasItems">
        <div *ngSwitchCase="true">
          <div *ngIf="config.isShowFilter" class="row">
            <div class="col-xs-12">
              <input class="form-control"
                     type="text"
                     placeholder="Filter"
                     style="font-size: 0.9rem; min-height: 2rem"
                     [formControl]="filterControl"/>
            </div>
          </div>
          <div class="items-container" *ngIf="hasFilterItems">
            <div *ngIf="config.isShowFilter || config.isShowCollapseExpand"
                 class="dropdown-divider"></div>
            <div class="treeview-container">
              <div *ngFor="let item of filterItems">
                <gp-treeview [item]="item" [transIdSeperator]="transIdSeperator" [checkParentOnAnyChild]="checkParentOnAnyChild" (checkedChange)="onItemCheckedChange(item, $event)"></gp-treeview>
              </div>
            </div>
          </div>
          <div *ngIf="!hasFilterItems" class="dropdown-item">
            No items found
          </div>
        </div>
        <div *ngSwitchCase="false" class="dropdown-item">
          No items
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./dropdown-interview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class DropdownTreeviewComponent extends Destroyable implements OnChanges, OnInit {
  @Input() public items: TreeItem[];
  @Input() public transIdSeperator;
  @Input() public config: DropdownTreeviewConfig = DefaultConfig;
  @Input() public isSorted: boolean = false;
  @Input() public asc: boolean = true;
  @Input() public checkParentOnAnyChild: boolean = false;
  @Input() public inline: boolean = false;
  @Output() public hide: EventEmitter<any> = new EventEmitter<any>();
  @Output() public selectedChange: EventEmitter<any[]> = new EventEmitter<any[]>();

  public filterControl: UntypedFormControl = new UntypedFormControl();

  public isOpen: boolean = false;
  public text: string;
  public transIdsDisplay: string;
  public filterText: string;
  public filterItems: TreeItem[];

  constructor(private gpTranslateService: GPTranslateService,
              private el: ElementRef,
              private cd: ChangeDetectorRef) {
    super();
  }

  @HostListener('keyup.esc')
  public onKeyupEsc() {
    if (this.isOpen) {
      this.isOpen = false;
      this.hide.emit();
    }
  }

  @HostListener('document:click', ['$event', '$event.target'])
  public onClickOutside(event: any, targetElement: HTMLElement): void {
    if (targetElement && document.contains(targetElement) && !this.el.nativeElement.contains(targetElement)) {
      if (this.isOpen) {
        this.isOpen = false;
        this.hide.emit();
      }
    }
  }

  get hasItems(): boolean {
    return !isNil(this.items) && this.items.length > 0;
  }

  get hasFilterItems(): boolean {
    return !isNil(this.filterItems) && this.filterItems.length > 0;
  }

  public ngOnInit(): void {
    this.gpTranslateService.onLangChange.pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe(() => {
      const checkedItems = this.getCheckedItems();
      this.updateText(checkedItems);
      this.sortItems();
    });

    this.filterControl
      .valueChanges
      .pipe(
        debounceTime(400),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((value: string) => {
        this.onFilterTextChange(value);
        this.cd.detectChanges();
      });
  }

  public ngOnChanges(changes: { [propertyName: string]: SimpleChange }) {
    let change = changes['config'];
    if (!isNil(change)) {
      if (!isNil(this.config)) {
        this.config = defaults(this.config, DefaultConfig);
      } else {
        this.config = defaults({}, DefaultConfig);
      }
    }

    change = changes.items;
    if (!isNil(change) && !isNil(this.items)) {
      this.updateFilterItems();
      this.onAfterSelectedChange();
    }
  }

  public getCheckedItems(): TreeItem[] {
    let checkedItems: TreeItem[] = [];
    if (this.items && this.items.length) {
      for (let i = 0; i < this.items.length; i++) {
        checkedItems = concat(checkedItems, this.items[i].getCheckedItems());
      }
    }
    return checkedItems;
  }

  public onButtonClick(event: MouseEvent) {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.hide.emit();
    }
  }

  public displayTransIds(transIds: string[]): string {
    return this.gpTranslateService.getDisplayTranslation(transIds, this.transIdSeperator);
  }

  public resetDropdown() {
    this.items.forEach(item => {
      item.updateCheckedRecursive(false);
      item.updateCollapsedRecursive(true);
    });
    this.updateText([]);
    this.filterControl.setValue(null);
    this.selectedChange.emit([]);
  }

  private onFilterTextChange(filterText: string) {
    this.filterText = filterText;
    this.updateFilterItems();
  }

  public onItemCheckedChange(item: TreeItem, checked: boolean) {
    if (item instanceof FilterTreeItem) {
      item.updateRefChecked();
    }
    this.onAfterSelectedChange();
  }

  private onAfterSelectedChange() {
    const checkedItems = this.getCheckedItems();
    this.updateText(checkedItems);

    const values = checkedItems.map(item => item.value);

    this.selectedChange.emit(values);
  }

  private getAllCheckedCount(): number {
    let count = 0;
    const countRecursive = (items: TreeItem[]) => {
      items.forEach(item => {
        if (item.checked) {
          count++;
        }
        if (!isNil(item.children)) {
          countRecursive(item.children);
        }
      });
    };
    if (this.items) {
      countRecursive(this.items);
    }
    return count;
  }

  private updateText(checkedItems: TreeItem[]) {
    if (checkedItems.length === 0) {
      this.text = this.config?.noSelectText ?? 'DROPDOWN.no_selected';
    } else if (checkedItems.length === 1) {
      this.text = checkedItems[0].text;
      this.transIdsDisplay = this.displayTransIds(checkedItems[0].transIds);
    } else {
      const count = this.config?.countAllChecked ? this.getAllCheckedCount() : checkedItems.length;
      const label = this.config?.moreSelectText ?? 'items selected';
      this.text = `${count} ${label}`;
    }
  }

  private updateFilterItems() {
    if (!isNil(this.filterText) && this.filterText !== '') {
      const filterItems: TreeItem[] = [];
      this.items.forEach(item => {
        const newItem = this.filterItem(item, this.filterText);
        if (!isNil(newItem)) {
          filterItems.push(newItem);
        }
      });
      this.filterItems = filterItems;
    } else {
      this.items.forEach((item: TreeItem) => item.updateCollapsedRecursive(true));
      this.filterItems = this.items;
    }
    if (this.isSorted) {
      this.sortItems();
    }
  }

  private filterItem(item: TreeItem, filterText: string): TreeItem {
    const itemText = item.text ? this.gpTranslateService.instant(item.text) : this.displayTransIds(item.transIds);
    const isMatch = includes(itemText.toLowerCase(), filterText.toLowerCase());
    if (isMatch) {
      item.updateCollapsedRecursive(true);
      return item;
    } else {
      if (isNil(item.children)) {
        return;
      }
      const children: TreeItem[] = [];
      let checkedCount = 0;
      item.children.forEach(child => {
        const newChild = this.filterItem(child, filterText);
        if (!isNil(newChild)) {
          children.push(newChild);
          if (newChild.checked) {
            checkedCount++;
          }
        }
      });
      if (children.length > 0) {
        item.collapsed = false;
        const newItem = new FilterTreeItem(item);
        newItem.children = children;
        newItem.checked = children.length === checkedCount;
        return newItem;
      }
    }

    return undefined;
  }

  private sortItems() {
    this.filterItems.sort((a: any, b: any) => {
      if (a.text && b.text) {
        return sortByAsc(this.gpTranslateService.instant(a.text) < this.gpTranslateService.instant(b.text), this.asc);
      } else {
        return sortByAsc(this.displayTransIds(a.transIds) < this.displayTransIds(b.transIds), this.asc);
      }
    });
  }
}
